import Box from '@mui/material/Box';
import { SystemStyleObject } from '@mui/system/styleFunctionSx/styleFunctionSx';
import React from 'react';

import StepCompleted from '@/assets/icons/steps-completed.svg?react';
import DashedCircle from '@/assets/icons/steps-pending.svg?react';
import { useTranslator } from '@/hooks/useTranslator';
import { Status } from '@/types/steps';

interface Props {
  readonly status: Status;
  readonly size?: 16 | 19;
  readonly active?: boolean;
}

export const StepProgressIndicator: React.FC<Props> = (props: Props): React.ReactElement => {
  const { status, size = 16, active = false } = props;

  const tr = useTranslator();

  const createStyle = React.useCallback(
    (color: string): SystemStyleObject => ({
      flexShrink: 0,
      width: size,
      height: size,
      color: color,
      borderRadius: '20px',
    }),
    [size],
  );

  const style = React.useMemo((): SystemStyleObject => {
    if (active) {
      return createStyle('primary.main');
    } else if ((status & Status.completed) === Status.completed) {
      return createStyle('text.primary');
    } else if (status === Status.initial) {
      return createStyle('text.primary');
    } else {
      throw new Error('Invalid status');
    }
  }, [active, createStyle, status]);

  if ((status & Status.completed) === Status.completed) {
    return (
      <Box role="status" aria-label={tr('COMPLETED_STATUS')} sx={style}>
        <StepCompleted width="100%" height="100%" fill="currentColor" />
      </Box>
    );
  } else {
    return (
      <Box role="status" aria-label={tr('INCOMPLETE_STATUS')} sx={style}>
        <DashedCircle stroke="currentColor" />
      </Box>
    );
  }
};
